import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";

export interface FormPageLayoutProps {
  /** Page title */
  title: string;
  /** Page subtitle */
  subtitle?: string;
  /** Form content */
  children: ReactNode;
  /** Additional className for the wrapper */
  className?: string;
  /** Back navigation link (optional) */
  backLink?: string;
  /** Back navigation label for accessibility (optional) */
  backLabel?: string;
}

interface FormPageHeaderProps {
  backLink: string;
  backLabel: string;
  title: string;
  subtitle?: string;
}

function FormPageHeader({ backLink, backLabel, title, subtitle }: FormPageHeaderProps) {
  return (
    <div className="flex items-center gap-3">
      <Link
        to={backLink}
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        aria-label={backLabel}
      >
        <ChevronLeft className="size-5" />
      </Link>
      <div className="flex flex-col">
        <h1 className="text-xl font-semibold leading-tight">{title}</h1>
        <span className="text-sm text-muted-foreground">{backLabel}</span>
        {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

/**
 * FormPageLayout - Layout for form pages that don't need data fetching states.
 *
 * Use this for simple form submission pages (e.g., issue reporting, contact forms).
 * Supports optional back navigation.
 *
 * @example
 * ```tsx
 * // With back navigation
 * const CreateRequest = () => {
 *   return (
 *     <FormPageLayout
 *       title="Nouvelle demande"
 *       subtitle="Remplissez le formulaire ci-dessous"
 *       backLink="/requests"
 *       backLabel="Retour à la liste"
 *     >
 *       <RequestForm onSubmit={handleSubmit} processing={processing} />
 *     </FormPageLayout>
 *   );
 * };
 *
 * // Without back navigation
 * const IssueReporting = () => {
 *   return (
 *     <FormPageLayout
 *       title="Nouveau signalement"
 *       subtitle="Signalez un problème ou faites une suggestion"
 *     >
 *       <IssueReportingForm onSubmit={handleSubmit} processing={processing} />
 *     </FormPageLayout>
 *   );
 * };
 * ```
 */
export function FormPageLayout({ title, subtitle, children, className, backLink, backLabel }: FormPageLayoutProps) {
  return (
    <PageWrapper className={className}>
      {backLink ? (
        <FormPageHeader backLink={backLink} backLabel={backLabel || "Retour"} title={title} subtitle={subtitle} />
      ) : (
        <PageHeader title={title} subtitle={subtitle} />
      )}
      {children}
    </PageWrapper>
  );
}
